/* Las salidas de Buenos Aires, de la Previsión a SALIDAS BITREN.
 *
 * Lautaro Díaz (Satelital), ticket #432, 16/10/2026, después de lo de las
 * Torrecillas de Río Grande: «LO MISMO SERIA CON LAS CARGAS DIARIAS DE GULA,
 * QUE SON LAS TORRECILLAS DE BUENOS AIRES. PONER LAS CARGAS DE GULA Y QUE SE
 * COMPLETE LA ONLY BITRENES».
 *
 * ── DE DÓNDE SALE ───────────────────────────────────────────────────────────
 *
 * Las cargas de Buenos Aires no vienen en un Excel: Tráfico las escribe en la
 * Previsión, que es de FlotaBot. FlotaBot ya junta los semis 15xx y los playos
 * 25xx en parejas, cada serie por su lado (`salidas_bitren` del bot: «1546/47»
 * en una fila, o «1588» y «1589» sueltos) y dice el titular; acá sólo se
 * escriben en la hoja de él, sin mirar la serie. Los playos van desde el
 * 19/10 (Lautaro: «SISI LOS BITRENES 1000 Y LOS 2000 VAN A SALIDAS BITREN.»).
 *
 * ── SALIDAS BITREN ──────────────────────────────────────────────────────────
 *
 * Su hoja: F/C · F/S · FUR · TRAC · CHOFERES · CLIENTES LUGAR DE CARGA · un
 * día por columna · UBICACIÓN ACTUALIZADA · F/ENGANCHE · PERNOCTE… · TARJ YPF.
 * Cada bitren, en dos filas: arriba el día de carga, el de salida, el par, el
 * tractor, el chofer y el cliente; abajo sólo el impar. Todas las columnas
 * menos FUR van combinadas de a dos, como las combina él. Lo demás (los días,
 * la ubicación, los pernoctes, la tarjeta) lo escribe él: no se toca nunca.
 * Nada de lo que se escribe queda debajo de una combinada suya (ver
 * `agregarASalidas`).
 *
 * El día de salida (F/S) es el mismo que el de carga (F/C), con el número del
 * día solo, como él lo escribe: lo que carga en Buenos Aires sale ese mismo
 * día (Lautaro, 19/10, ticket #445: «LO QUE CARGA EN BUE HOY TAMBIÉN SALE
 * HOY»). No como en ARRIBOS BITREN, donde sale al día siguiente. Si la
 * hoja no tiene F/S, no se escribe en ningún lado.
 *
 * Se lee por el título y no por la posición, como `shared/torrecillas`: si
 * mañana suma una columna, sigue andando.
 *
 * Todo acá es puro: lo prueban los tests con datos inventados.
 */

import { colALetra } from "./planilla";
import { normalizarCombinadas, rangoCombinada } from "./planillaCombinadas";
import { pelado } from "./torrecillas";

const txt = (v) => String(v ?? "").replace(/\s+/g, " ").trim();

/* El título pelado (sin acentos, espacios ni signos) → la columna. */
const COLUMNAS = {
  fc: (p) => p === "FC",
  fs: (p) => p === "FS",
  fur: (p) => ["FUR", "FURGON", "BITREN"].includes(p),
  trac: (p) => ["TRAC", "TRA", "TRACTOR"].includes(p),
  chofer: (p) => ["CHOFER", "CHOFERES"].includes(p),
  // «CLIENTES LUGAR DE CARGA»: el resto del título cambia según quién la armó.
  cliente: (p) => p.startsWith("CLIENTE"),
};

/* `{fila, cols, ultima}` del encabezado de SALIDAS BITREN (en las primeras
   seis filas), o null. `ultima` es la última columna con título: hasta ahí
   llegan las combinadas. Para leer choferes alcanza con FUR y CHOFER; para
   escribir (`conFC`) además tiene que estar F/C: sin el día no se sabe qué
   bitren ya está, y se repetirían. */
export function encabezadoSalidas(hoja, { conFC = false } = {}) {
  const celdas = hoja?.celdas || [];
  for (let i = 0; i < Math.min(celdas.length, 6); i++) {
    const cols = {};
    let ultima = -1;
    (celdas[i] || []).forEach((v, j) => {
      const p = pelado(v);
      if (p) ultima = j;
      for (const [k, es] of Object.entries(COLUMNAS)) {
        if (cols[k] === undefined && p && es(p)) cols[k] = j;
      }
    });
    if (cols.fur !== undefined && cols.chofer !== undefined && (!conFC || cols.fc !== undefined)) {
      return { fila: i, cols, ultima };
    }
  }
  return null;
}

const paraEscribir = (hoja) => encabezadoSalidas(hoja, { conFC: true });

/* La hoja de salidas de una planilla del hub (`datos.hojas`), o -1. Sólo
   una en la que se pueda escribir: con F/C, FUR y CHOFER. */
export function hojaDeSalidas(datos) {
  const hojas = datos?.hojas || [];
  return hojas.findIndex((h) => pelado(h.nombre).includes("SALIDA") && paraEscribir(h));
}

/* Cómo escribe él el cliente que Tráfico escribe largo:
   «RADIO VICTORIA (LOG. CENTRAL)» → «R.VICTORIA LOG CENTRAL»,
   «CARRIER LOPRIMO 1» → «CARRIER LOPRIMO». Lo mismo que se ve en su hoja; lo
   que no se reconoce queda como vino, y en la vista previa lo corrige. */
export function clienteCorto(v) {
  const s = txt(String(v ?? "").toUpperCase().replace(/[()]/g, " ").replace(/\bLOG\.\s*/g, "LOG "))
    .replace(/^RADIO VICTORIA\b/, "R.VICTORIA");
  return s.replace(/\s+\d$/, "");
}

/* `{tractor: chofer}` con el nombre como él lo escribió en sus hojas de
   arribos y salidas (la última vez que lo escribió). La Previsión trae
   «ZAVALA» o «TABORDA M»; él escribe «ZAVALA MARCELO». */
export function choferesDeLaPlanilla(datos) {
  const out = {};
  for (const h of datos?.hojas || []) {
    // Las dos hojas (ARRIBOS y SALIDAS) tienen el mismo par de títulos.
    const enc = encabezadoSalidas(h);
    if (!enc) continue;
    const ct = enc.cols.trac;
    const cc = enc.cols.chofer;
    if (ct === undefined || cc === undefined) continue;
    for (const r of (h.celdas || []).slice(enc.fila + 1)) {
      const t = txt(r?.[ct]);
      const c = txt(r?.[cc]).toUpperCase();
      if (/^\d+$/.test(t) && c) out[t] = c;
    }
  }
  return out;
}

/* El chofer como lo escribe él, si es el mismo: el apellido (la primera
   palabra) tiene que coincidir. Si no, el de la Previsión, en mayúscula. */
export function choferLargo(chofer, tractor, conocidos = {}) {
  const c = txt(chofer).toUpperCase();
  const suyo = conocidos[txt(tractor)];
  if (!c || !suyo) return c;
  return pelado(c.split(" ")[0]) === pelado(suyo.split(" ")[0]) ? suyo : c;
}

/* Sin tractor porque el de bitren todavía está «a confirmar» en la
   Previsión (FlotaBot sólo pone el titular si está confirmado). */
export function aConfirmar(b) {
  return !txt(b?.tractor) && (b?.previsto || []).some((p) => p && p.confirmado === false);
}

/* La nota de Tráfico sin el «a confirmar: (…)» que ya se dice aparte. Ése
   lo agrega FlotaBot al leer el Excel, siempre con los dos puntos; lo que
   escribió Tráfico de su mano —«A CONFIRMAR C/BARRALES», el 15/10, al lado
   de un furgón sin tractor— no lleva y se muestra. */
export function notaDeTrafico(nota) {
  return txt(nota)
    .split(" · ")
    .map(txt)
    .filter((x) => x && !/^a confirmar\s*:/i.test(x))
    .join(" · ");
}

/* Los bitrenes que manda FlotaBot → lo que se muestra y se escribe. */
export function filasDeSalidas(bitrenes, conocidos = {}) {
  return (bitrenes || []).map((b) => ({
    primera: txt(b.primera),
    segunda: txt(b.segunda),
    tractor: txt(b.tractor),
    chofer: choferLargo(b.chofer, b.tractor, conocidos),
    cliente: clienteCorto(b.cliente),
    previsto: b.previsto || [],
  }));
}

const seCruzan = (a, b) => a.r0 <= b.r1 && b.r0 <= a.r1 && a.c0 <= b.c1 && b.c0 <= a.c1;

const diaDe = (fecha) => String(Number(String(fecha || "").split("-")[2] || ""));

/* Los bitrenes que la hoja ya tiene: `F/C|FUR`. La 2da fila de cada bitren no
   repite el F/C (está combinado), así que se arrastra el de arriba. */
function yaEnLaHoja(hoja, enc) {
  const { fila, cols } = enc;
  const hay = new Set();
  let fcArriba = "";
  (hoja.celdas || []).forEach((r, i) => {
    if (i <= fila || !r) return;
    const f = txt(r[cols.fc]);
    if (f) fcArriba = f;
    const fur = txt(r[cols.fur]);
    if (fur) hay.add(`${f || fcArriba}|${fur}`);
  });
  return hay;
}

/* Cuáles de `filas` ya están en la hoja ese día (por cualquiera de los dos
   semis): `Set` de `primera`. Para la vista previa. */
export function cualesYaEstan(hoja, filas, fecha) {
  const enc = paraEscribir(hoja);
  if (!enc) return new Set();
  const hay = yaEnLaHoja(hoja, enc);
  const fc = diaDe(fecha);
  return new Set(
    (filas || [])
      .filter((b) => hay.has(`${fc}|${b.primera}`) || hay.has(`${fc}|${b.segunda}`))
      .map((b) => b.primera),
  );
}

/* Lo que escribe un bitren en SALIDAS BITREN: arriba F/C, F/S, FUR, TRAC,
   CHOFERES y CLIENTES; abajo, sólo FUR. Todas menos FUR van combinadas de a
   dos con el valor arriba, así que en las dos filas esas celdas son suyas. */
const HUELLA = ["fc", "fs", "fur", "trac", "chofer", "cliente"];

/* Suma a la hoja de salidas los bitrenes que no tiene, abajo de lo último
   escrito. Devuelve `{hoja, agregados, yaEstaban, salteadas, nuevas,
   bitrenes, estaban}` (sin `salteadas` si la hoja no es para escribir); la hoja que
   entra no se toca. `nuevas` son los renglones que se escribieron (índices
   desde 0, los dos de cada bitren): ahí después va la UBICACIÓN
   (`shared/ubicacionAlAgregar`). `bitrenes` son los de `filas` que se
   agregaron y `estaban` los que ya estaban ese día: los dos van después a
   Enganches (`shared/salidasEnganches`), que no duplica.
   No escribe nunca en una celda que tenga algo: las filas que usa están
   vacías porque van después de la última con algo.

   SUS COMBINADAS: él deja preparados bloques vacíos de dos filas ya
   combinados (A15:A16, B15:B16…), y combina a mano lo que quiere
   («Combinar y centrar», 19/10). Escribir debajo de una combinada suya deja
   el valor donde no se ve. Así que cada bitren va en las primeras dos filas
   vacías donde ninguna combinada suya toca sus celdas (F/C, F/S, FUR, TRAC,
   CHOFERES y CLIENTES, en esas dos filas), salvo las de una sola columna
   justo de esas dos filas, que son las que se iban a poner: ésas se usan y
   no se repiten (en FUR ni ésa: el impar va abajo). Como en ARRIBOS BITREN
   (`agregarAArribos`): una suya de dos filas y varias columnas (D15:E16,
   TRAC y CHOFERES) también lo hace bajar, aunque sea de esas dos filas. En
   las demás columnas hasta la última con título (los días, la ubicación,
   los pernoctes, la tarjeta) el bitren no escribe, pero las combina de a
   dos: ahí una suya que agarra otras filas también lo hace bajar, y una de
   esas dos filas no (gana la suya, la nueva no entra). Si escribió sólo en
   la de arriba de un bloque (el F/C, o un semi suelto), la siguiente es la
   de abajo del bloque: tampoco sirve. Más abajo siempre hay lugar (la hoja
   crece), así que se agrega igual; las filas que se saltean quedan como
   estaban y se devuelven en `salteadas` (números de fila, como se ven) para
   decirlo en la pantalla.

   EL F/S: el mismo día que el F/C y, como el F/C, sólo en la fila de arriba
   del bitren nuevo. Ni los que ya estaban ni lo que él escribió se tocan:
   tampoco una celda con algo de las filas que se usan. */
export function agregarASalidas(hoja, filas, fecha) {
  const enc = paraEscribir(hoja);
  if (!enc) return { hoja, agregados: 0, yaEstaban: 0, nuevas: [], bitrenes: [], estaban: [] };
  const { fila, cols, ultima: ultimaCol } = enc;
  const suyas = hoja.combinadas || [];
  const deEl = normalizarCombinadas(suyas).map(rangoCombinada);
  const huella = HUELLA.map((k) => cols[k]).filter((c) => c !== undefined);
  const tocaLaHuella = (m) => huella.some((c) => m.c0 <= c && c <= m.c1);
  const deEsasDos = (m, r) => m.r0 === r && m.r1 === r + 1;
  // La que el bitren iba a poner: una columna, justo esas dos filas, no FUR.
  const esLaDeDos = (m, r) => deEsasDos(m, r) && m.c0 === m.c1 && m.c0 !== cols.fur;
  const sirve = (r) => !deEl.some(
    (m) => m.c0 <= ultimaCol && m.r0 <= r + 1 && m.r1 >= r
      && !esLaDeDos(m, r) && (tocaLaHuella(m) || !deEsasDos(m, r)),
  );
  const fc = diaDe(fecha);
  const celdas = (hoja.celdas || []).map((r) => [...(r || [])]);
  const ancho = Math.max(hoja.cols || 0, ...celdas.map((r) => r.length));
  const hay = yaEnLaHoja(hoja, enc);
  let ultima = fila;
  celdas.forEach((r, i) => {
    if (i > fila && r.some((v) => txt(v))) ultima = i;
  });
  const nuevas = [];
  const escritas = [];
  const agregadosB = [];
  const estaban = [];
  const salteadas = [];
  let agregados = 0;
  let yaEstaban = 0;
  let en = ultima + 1;
  const vacia = () => Array.from({ length: ancho }, () => "");
  for (const b of filas || []) {
    if (hay.has(`${fc}|${b.primera}`) || hay.has(`${fc}|${b.segunda}`)) {
      yaEstaban += 1;
      estaban.push(b);
      continue;
    }
    while (!sirve(en)) {
      salteadas.push(en + 1);
      en += 1;
    }
    // Arriba el par con todo lo del bitren (el F/S, el día del F/C); abajo,
    // el impar solo.
    const arriba = { fur: b.primera, fc, fs: fc, trac: b.tractor, chofer: b.chofer, cliente: b.cliente };
    const abajo = { fur: b.segunda };
    for (const [k, valores] of [[0, arriba], [1, abajo]]) {
      const r = celdas[en + k] ? [...celdas[en + k]] : vacia();
      while (r.length < ancho) r.push("");
      for (const [campo, v] of Object.entries(valores)) {
        if (cols[campo] !== undefined && v && !txt(r[cols[campo]])) r[cols[campo]] = v;
      }
      celdas[en + k] = r;
    }
    for (let c = 0; c <= ultimaCol; c++) {
      if (c !== cols.fur) nuevas.push(`${colALetra(c)}${en + 1}:${colALetra(c)}${en + 2}`);
    }
    hay.add(`${fc}|${b.primera}`);
    hay.add(`${fc}|${b.segunda}`);
    escritas.push(en, en + 1);
    agregadosB.push(b);
    en += 2;
    agregados += 1;
  }
  for (let i = 0; i < celdas.length; i++) if (!celdas[i]) celdas[i] = vacia();
  // Las suyas quedan tal cual. Una nueva que se cruza con una de él (porque
  // ya combinó a mano esas filas vacías) no entra: gana la suya.
  const tomadas = [...deEl];
  const entran = nuevas.filter((txtRango) => {
    const rg = rangoCombinada(txtRango);
    if (tomadas.some((t) => seCruzan(t, rg))) return false;
    tomadas.push(rg);
    return true;
  });
  const combinadas = agregados ? [...suyas, ...entran] : hoja.combinadas;
  return {
    hoja: {
      ...hoja,
      celdas,
      ...(combinadas ? { combinadas } : {}),
      filas: Math.max(hoja.filas || 0, celdas.length),
      cols: ancho,
    },
    agregados,
    yaEstaban,
    salteadas,
    nuevas: escritas,
    bitrenes: agregadosB,
    estaban,
  };
}
